import { useState } from 'react';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import Drawer from '../../../Common/Drawer';
import { DatePickerInputFormik, TimeInputFormik, InputFormik, TextAreaFormik } from '../../../Common';
import { selectUserSub } from '../../../../store/slices/auth.slice';
import { useHttpPostMeetActivity, useHttpPostNoteActivity } from '../../../../hooks';
import { meetValidationSchema, noteValidationSchema } from './validationSchema';
import { ActivityType } from '../../../../commonTypes/project.types';

interface IActivityDrawerProps {
    open: boolean;
    onClose: () => void;
    projectId: string;
    clientId: string;
    onSuccess?: (activity: any) => void;
}

interface IActivityFormValues {
    title: string;
    note: string;
    description: string;
    date: string;
    time: string;
    meetLink: string;
}

const ActivityDrawer = ({ open, onClose, projectId, clientId, onSuccess }: IActivityDrawerProps) => {
    const [activeTab, setActiveTab] = useState<ActivityType>(ActivityType.NOTE);
    const userSub = useSelector(selectUserSub);
    const { execSubmit: createNoteActivity } = useHttpPostNoteActivity();
    const { execSubmit: createMeetActivity } = useHttpPostMeetActivity();

    const context = useFormik<IActivityFormValues>({
        initialValues: {
            title: '',
            note: '',
            description: '',
            date: '',
            time: '',
            meetLink: '',
        },
        validationSchema: activeTab === ActivityType.NOTE ? noteValidationSchema : meetValidationSchema,
        enableReinitialize: true,
        onSubmit: async values => {
            try {
                if (activeTab === ActivityType.NOTE) {
                    createNoteActivity(
                        {
                            ...(projectId && { projectId }),
                            clientId,
                            title: values.title,
                            description: values.note,
                            createdBy: userSub,
                        },
                        {
                            onSuccess: createdActivity => {
                                handleClose();
                                if (onSuccess) onSuccess(createdActivity);
                            },
                        }
                    );
                } else {
                    createMeetActivity(
                        {
                            ...(projectId && { projectId }),
                            clientId,
                            title: values.title,
                            description: values.description,
                            date: values.date,
                            time: values.time,
                            link: values.meetLink,
                            createdBy: userSub,
                        },
                        {
                            onSuccess: createdActivity => {
                                handleClose();
                                if (onSuccess) onSuccess(createdActivity);
                            },
                        }
                    );
                }
            } catch (_) {}
        },
    });

    const handleClose = () => {
        context.resetForm();
        setActiveTab(ActivityType.NOTE);
        onClose();
    };

    const isFormValid = () => {
        if (activeTab === ActivityType.NOTE) {
            return context.values.title.trim() !== '' && context.values.note.trim() !== '';
        } else {
            return (
                context.values.title.trim() !== '' &&
                context.values.description.trim() !== '' &&
                context.values.date.trim() !== '' &&
                context.values.time.trim() !== ''
            );
        }
    };

    return (
        <Drawer
            open={open}
            title="Add New Activity"
            onClose={handleClose}
            showFooter={true}
            className="activity-drawer"
            rightButtons={[
                {
                    text: 'Cancel',
                    variant: 'text',
                    onClick: handleClose,
                },
                {
                    text: 'Add New Activity',
                    variant: 'contained',
                    onClick: () => context.handleSubmit(),
                    disabled: !isFormValid(),
                },
            ]}
        >
            <div className="activity-drawer-content">
                <div className="activity-drawer-tabs">
                    <button
                        type="button"
                        className={`activity-drawer-tab ${activeTab === ActivityType.NOTE ? 'activity-drawer-tab--active' : ''}`}
                        onClick={() => setActiveTab(ActivityType.NOTE)}
                    >
                        <img src="/svg/note.svg" alt="note" className="activity-drawer-tab__icon" />
                        Note
                    </button>
                    <button
                        type="button"
                        className={`activity-drawer-tab ${activeTab === ActivityType.MEET ? 'activity-drawer-tab--active' : ''}`}
                        onClick={() => setActiveTab(ActivityType.MEET)}
                    >
                        <img src="/svg/meet.svg" alt="meet" className="activity-drawer-tab__icon" />
                        Meet
                    </button>
                </div>

                <div className="activity-drawer-form">
                    {activeTab === ActivityType.NOTE ? (
                        <>
                            <InputFormik
                                context={context}
                                id="title"
                                label="Note Title"
                                placeholder="i.e. Project Review"
                                value={context.values.title}
                                onChange={context.handleChange}
                                isRequired
                            />

                            <TextAreaFormik context={context} id="note" label="Note" placeholder="Enter note text..." isRequired />
                        </>
                    ) : (
                        <>
                            <InputFormik
                                context={context}
                                id="title"
                                label="Meet Title"
                                placeholder="i.e. Project Review"
                                value={context.values.title}
                                onChange={context.handleChange}
                                isRequired
                            />

                            <TextAreaFormik
                                context={context}
                                id="description"
                                label="Meet Description"
                                placeholder="Enter description..."
                                isRequired
                            />

                            <DatePickerInputFormik
                                context={context as any}
                                id="date"
                                label="Select Date"
                                placeholder="Please Select"
                                isRequired
                                date={context.values.date ? new Date(context.values.date) : undefined}
                                changeDate={date => {
                                    const jsDate = date?.toJSDate();
                                    if (jsDate) {
                                        const year = jsDate.getFullYear();
                                        const month = String(jsDate.getMonth() + 1).padStart(2, '0');
                                        const day = String(jsDate.getDate()).padStart(2, '0');
                                        const formattedDate = `${year}-${month}-${day}`;
                                        context.setFieldValue('date', formattedDate).catch(() => {});
                                    } else {
                                        context.setFieldValue('date', '').catch(() => {});
                                    }
                                }}
                                onPeriodChange={() => {}}
                                enablePastDays={true}
                            />

                            <TimeInputFormik
                                context={context as any}
                                id="time"
                                label="Select Time"
                                placeholder="Please Select"
                                onTimeChange={(time: string) => context.setFieldValue('time', time)}
                            />

                            <InputFormik
                                context={context}
                                id="meetLink"
                                label="Meet Link"
                                placeholder="Enter meeting link (e.g., Zoom, Google Meet)"
                                value={context.values.meetLink}
                                onChange={context.handleChange}
                            />
                        </>
                    )}
                </div>
            </div>
        </Drawer>
    );
};

export default ActivityDrawer;
